import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ApiTask, ApiTaskStatus } from "@/types/task.types.ts";
import { ApiEsignRequest } from "@/types/esign.types.ts";
import { EsignRequestRow } from "@/pages/esign/widgets/EsignRequestRow.tsx";
import { TaskViewModal } from "@/pages/tasks/widgets/TaskViewModal.tsx";
import {
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  ListItem,
  Skeleton,
  Stack,
  Tag,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface TasksSectionContentProps {
  title: string;
  tasks: ApiTask[];
  esignRequests?: ApiEsignRequest[];
  isLoading?: boolean;
  emptyState?: ReactNode;
  titleWarning?: boolean;
}

export function TasksSectionContent({
  title,
  tasks,
  esignRequests = [],
  isLoading = false,
  titleWarning = false,
  emptyState = undefined,
}: TasksSectionContentProps) {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();

  const handleViewTask = (task: ApiTask) => {
    openModal(TaskViewModal, { task });
  };

  const hasContent = tasks.length > 0 || esignRequests.length > 0;

  return (
    <Box>
      {isLoading ? (
        <Stack gap={"sm"}>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className={"h-20 w-auto"} />
          ))}
        </Stack>
      ) : hasContent ? (
        <>
          <Title
            header={title}
            size={"xs"}
            paddingBottom
            className={titleWarning ? "text-warning" : undefined}
          />
          <Stack gap={"sm"}>
            {tasks.map((task) => {
              const isCompleted = task.status === ApiTaskStatus.completed;
              const isInProgress = task.status === ApiTaskStatus.in_progress;
              let caption: string | undefined;
              if (task.created_by) {
                caption = t("tasks:from_employee", { name: renderEmployeeName(task.created_by) });
              } else if (task.created_by_workflow) {
                caption = t("tasks:from_workflow", { title: task.created_by_workflow.title });
              }
              return (
                <Box key={task.id} variant={"whiteBorder"} className={"bg-light px-6 py-5"}>
                  <ListItem
                    icon={
                      isCompleted ? (
                        <Avatar
                          color={"positive"}
                          colorStyle={"solid"}
                          icon={<FontAwesomeIcon icon="fa-light fa-check" className="text-white" />}
                        />
                      ) : (
                        <Avatar icon={<FontAwesomeIcon icon="fa-light fa-check" />} />
                      )
                    }
                    titleSlot={
                      <Text
                        className={twMerge("font-semibold", isCompleted ? "text-secondary" : "")}
                      >
                        {task.related_employee
                          ? `${task.title} (${renderEmployeeName(task.related_employee)})`
                          : task.title}
                      </Text>
                    }
                    caption={caption}
                    valueSlot={
                      <Stack direction={"horizontal"} gap={"sm"} items={"center"}>
                        {isInProgress && (
                          <Tag variant={"new"}>{t("tasks:task_status_in_progress")}</Tag>
                        )}
                        <Button
                          variant="plain"
                          rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
                          onClick={() => handleViewTask(task)}
                        >
                          <Text className={"font-semibold"}>{t("tasks:view_task")}</Text>
                        </Button>
                      </Stack>
                    }
                    behavior={"card"}
                  />
                </Box>
              );
            })}
            {esignRequests.map((esignRequest) => (
              <Box
                key={`esign-${esignRequest.public_id}`}
                variant={"whiteBorder"}
                className={"bg-light px-6 py-5"}
              >
                <EsignRequestRow esignRequest={esignRequest} />
              </Box>
            ))}
          </Stack>
        </>
      ) : (
        emptyState
      )}
    </Box>
  );
}
